<template>
	<!-- Both sources and both layers stay mounted at all times. The district
	     layer (layerId, default "service-area-outline") is the `before:` anchor
	     for the mosquito-risk, parcel and planet/stadia raster layers, so it
	     must always exist in the maplibre style. Toggling uses
	     layout.visibility instead of v-if for that reason. -->
	<mgl-geo-json-source source-id="service-area" :data="serviceAreaGeoJSON">
		<mgl-line-layer
			:before="before"
			:layer-id="layerId"
			:layout="{
				visibility: showDistrictBoundary ? 'visible' : 'none',
			}"
			:paint="{
				'line-color': '#0d6efd',
				'line-width': 2.5,
				'line-opacity': 0.8,
			}"
		/>
	</mgl-geo-json-source>

	<mgl-geo-json-source source-id="satellite-area" :data="satelliteAreaGeoJSON">
		<mgl-line-layer
			:before="layerId"
			layer-id="satellite-area-outline"
			:layout="{ visibility: showAoiBoundary ? 'visible' : 'none' }"
			:paint="{
				'line-color': '#6f42c1',
				'line-width': 2,
				'line-opacity': 0.7,
				'line-dasharray': [4, 3],
			}"
		/>
	</mgl-geo-json-source>
</template>

<script setup lang="ts">
import { onMounted, ref } from "vue";
import { MglGeoJsonSource, MglLineLayer } from "vue-maplibre-gl";
import { useSessionStore } from "@/store/session";
import { apiClient } from "@/client";
import type { OrganizationResponse } from "@/type/api";
import type { GeoJSON } from "geojson";

interface Emits {
	(e: "loaded", satelliteGeometry: GeoJSON.GeoJSON | null): void;
	(e: "service-area-loaded", serviceAreaGeometry: GeoJSON.GeoJSON | null): void;
}
interface Props {
	before?: string;
	layerId?: string;
	// Visibility toggles. Both default to true so the boundaries keep showing
	// for callers that don't pass them — i.e. today's always-visible behaviour.
	showDistrictBoundary?: boolean;
	showAoiBoundary?: boolean;
}
const emit = defineEmits<Emits>();
const props = withDefaults(defineProps<Props>(), {
	layerId: "service-area-outline",
	showDistrictBoundary: true,
	showAoiBoundary: true,
});
const session = useSessionStore();

const emptyFC: GeoJSON.FeatureCollection = {
	type: "FeatureCollection",
	features: [],
};
const serviceAreaGeoJSON = ref<GeoJSON.GeoJSON>(emptyFC);
const satelliteAreaGeoJSON = ref<GeoJSON.GeoJSON>(emptyFC);

onMounted(async () => {
	const orgId = session.organization?.id;
	if (!orgId) return;

	try {
		const org = await apiClient.JSONGet<OrganizationResponse>(
			`/api/organization/${orgId}`,
		);
		if (org.serviceArea?.geometry) {
			serviceAreaGeoJSON.value = org.serviceArea.geometry;
		}
		if (org.satelliteSurveillanceArea?.geometry) {
			satelliteAreaGeoJSON.value = org.satelliteSurveillanceArea.geometry;
		}
		emit("loaded", satelliteAreaGeoJSON.value);
		emit("service-area-loaded", org.serviceArea?.geometry ?? null);
	} catch {
		// silently ignore if endpoints aren't available
	}
});
</script>
